import { DomainView } from "@/components/domain";
import { Skeleton } from "@/components/ui/skeleton";
import { useSheetContext } from "@/context/sheet";
import { useGetGuide } from "@/hooks/guides";
import { useGetRegion } from "@/hooks/regions";
import { useDeleteTour, useGetTour } from "@/hooks/tours";
import { T } from "@/i18n";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { Key } from "react";
import { format } from "date-fns";
import { DisplayKv } from "@/components/display-kv";

export const Route = createFileRoute("/(tours)/tours/_sheet/$id/")({
  component: RouteComponent,
});

function RouteComponent() {
  const { id } = Route.useParams();
  const { data, isLoading } = useGetTour(id);
  const { data: region, isLoading: regionLoading } = useGetRegion(
    data?.region?.toString() ?? "",
    {
      enabled: typeof data?.region === "number",
    }
  );
  const { data: guide, isLoading: guideLoading } = useGetGuide(
    data?.tourLeaderId?.toString() ?? "",
    {
      enabled: typeof data?.tourLeaderId === "number",
    }
  );
  const { close } = useSheetContext();
  const navigate = useNavigate();
  const { mutate: deleteTour } = useDeleteTour({
    onSuccess: () => {
      close(() => {
        navigate({ to: "/tours" });
      });
    },
  });

  return (
    <DomainView
      domain="tours"
      editLink={(children, key) => (
        <Link to="/tours/$id/edit" params={{ id }} key={key as Key}>
          {children}
        </Link>
      )}
      isLoading={isLoading}
      title={data?.name}
      onDelete={() => deleteTour(id)}
      propsList={[
        {
          title: "tours.fields.category",
          value: data?.category ? (
            <T tKey={`tours.categories.${data.category}`} />
          ) : (
            "-"
          ),
        },
        {
          title: "tours.fields.type",
          value: data?.type ? <T tKey={`tours.types.${data.type}`} /> : "-",
        },
        {
          title: "tours.fields.region",
          value:
            typeof data?.region !== "number" ? (
              "-"
            ) : regionLoading || !region ? (
              <Skeleton className="h-5 w-14" />
            ) : (
              region.name
            ),
        },
        {
          title: "tours.fields.operationRegion",
          value: data?.operationRegion ? (
            <T tKey={`common.operationRegions.${data?.operationRegion}`} />
          ) : (
            "-"
          ),
        },
        {
          title: "tours.fields.tourLeaderId",
          value:
            typeof data?.tourLeaderId !== "number" ? (
              "-"
            ) : guideLoading || !guide ? (
              <Skeleton className="h-5 w-14" />
            ) : (
              guide.name
            ),
        },
        {
          title: "tours.fields.active",
          value: <T tKey={`common.boolean.${!!data?.active}`} />,
        },
        {
          title: "tours.fields.exclusivity",
          value: <T tKey={`common.boolean.${!!data?.exclusivity}`} />,
        },
        {
          title: "tours.fields.fixedPrice",
          value: data?.fixedPrice ?? "-",
        },
        {
          title: "tours.fields.soloTravelerSupplement",
          value: data?.soloTravelerSupplement ?? "-",
        },
        {
          title: "tours.fields.percentageMap",
          value: (
            <DisplayKv
              list={Object.entries(data?.percentageMap ?? {}).map(
                ([key, value]) => ({ label: key, value })
              )}
            />
          ),
        },
        {
          title: "tours.fields.seasonalSupplementMap",
          value: (
            <DisplayKv
              list={Object.entries(data?.seasonalSupplementMap ?? {}).map(
                ([key, value]) => ({
                  label: key
                    .split(" - ")
                    .filter(Boolean)
                    .map((t) => format(new Date(+t), "dd/MM/yyyy"))
                    .join(" - "),
                  value,
                })
              )}
            />
          ),
        },
        {
          title: "tours.fields.individualRoomSupplementMap",
          value: (
            <DisplayKv
              list={Object.entries(data?.individualRoomSupplementMap ?? {}).map(
                ([key, value]) => ({
                  label: key
                    .split(" - ")
                    .filter(Boolean)
                    .map((t) => format(new Date(+t), "dd/MM/yyyy"))
                    .join(" - "),
                  value,
                })
              )}
            />
          ),
        },
        {
          title: "tours.fields.details",
          value: data?.details ?? "-",
        },
      ]}
    />
  );
}
